/**
 * PortalContext
 *
 * Provides portalType and branding information to all auth portal pages.
 * Wraps usePlatformConfig to expose a simple context that child components
 * can consume to decide which UI variant to render.
 *
 * Defaults to the agent portal experience when:
 *  - No client_id is present in the URL
 *  - The platform config fetch fails
 *  - The platform config is still loading (renders a loading state)
 *
 * Requirements: 4.1, 4.2, 4.3, 4.5
 */

import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { usePlatformConfig } from '../hooks/usePlatformConfig';
import type { PlatformBranding, PlatformConfig, PortalType } from '../types/platform.types';

export interface PortalContextValue {
  /** Which portal variant to render */
  portalType: PortalType;
  /** Optional branding from the platform config */
  branding: PlatformBranding | undefined;
  /** Full platform config (null when unavailable) */
  platformConfig: PlatformConfig | null;
  /** Whether MFA is required for this platform */
  mfaRequired: boolean;
  /** True while the platform config is being fetched */
  isLoading: boolean;
  /** Error message if the platform config fetch failed */
  error: string | null;
  /** The client_id extracted from URL params */
  clientId: string;
}

const PortalContext = createContext<PortalContextValue | undefined>(undefined);

interface PortalProviderProps {
  children: ReactNode;
}

/**
 * Provider that fetches platform config and exposes portal type + branding.
 */
export const PortalProvider: React.FC<PortalProviderProps> = ({ children }) => {
  const { config, isLoading, error, clientId } = usePlatformConfig();

  const value = useMemo<PortalContextValue>(() => {
    // Default to 'agent' when config is unavailable (requirement 4.5)
    const resolvedPortalType = config?.portalType ?? 'agent';
    // MFA defaults: true for agent platforms, false for customer platforms
    const resolvedMfaRequired = config?.mfaRequired ?? (resolvedPortalType === 'agent');

    return {
      portalType: resolvedPortalType,
      branding: config?.branding,
      platformConfig: config,
      mfaRequired: resolvedMfaRequired,
      isLoading,
      error,
      clientId,
    };
  }, [config, isLoading, error, clientId]);

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
};

/**
 * Hook to consume the PortalContext.
 *
 * @throws Error if used outside a PortalProvider
 */
export function usePortal(): PortalContextValue {
  const context = useContext(PortalContext);
  if (context === undefined) {
    throw new Error('usePortal must be used within a PortalProvider');
  }
  return context;
}
